import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiLink, FiMoon, FiSun } from 'react-icons/fi';
import { FaXTwitter, FaLinkedin, FaFacebook, FaGithub } from 'react-icons/fa6';
import TerminalBlock from './TerminalBlock';
import BlogArticleShell from './BlogArticleShell';
import { getExploreBlogs } from './blogSidebarData';
import { estimateReadTimeFromSections } from './blogReadTime';

const BLOG_THEME_STORAGE_KEY = 'sr-blog-theme';

const getInitialBlogTheme = () => {
  if (typeof window === 'undefined') {
    return true;
  }

  const storedTheme = window.localStorage.getItem(BLOG_THEME_STORAGE_KEY);
  if (storedTheme === 'light') {
    return false;
  }

  return true;
};

const articleSections = [
  {
    id: 'what-is-docker',
    title: 'What is Docker?',
    paragraphs: [
      'Modern development is not only about writing code. It is also about building, shipping, and running applications reliably in every environment.',
      'Docker packages applications and all dependencies into containers. A container is a lightweight, portable runtime that behaves consistently on laptops, servers, and cloud platforms.',
      'This consistency removes environment drift and helps teams move faster with fewer deployment surprises.',
    ],
  },
  {
    id: 'why-docker',
    title: 'Why Developers Use Docker',
    paragraphs: [
      'Docker is a practical workflow upgrade. It shortens setup time and reduces dependency conflicts across projects.',
    ],
    bullets: [
      'Consistency across development, testing, and production',
      'Isolation so projects do not conflict with each other',
      'Portability across local machines and cloud environments',
      'Lightweight execution compared to full virtual machines',
      'Scalability that fits microservice-based architecture',
    ],
  },
  {
    id: 'core-concepts',
    title: 'Core Concepts You Must Understand',
    subSections: [
      {
        title: 'Image',
        paragraphs: ['A read-only blueprint of your application and its dependencies.'],
      },
      {
        title: 'Container',
        paragraphs: ['A running instance of an image. This is your live application process.'],
      },
      {
        title: 'Dockerfile',
        paragraphs: ['A build script that defines how to construct your image step by step.'],
      },
      {
        title: 'Volume',
        paragraphs: ['Persistent storage that survives container deletion and recreation.'],
      },
      {
        title: 'Network',
        paragraphs: ['A communication layer that allows containers to reach each other safely.'],
      },
    ],
  },
  {
    id: 'how-it-works',
    title: 'How Docker Works',
    ordered: [
      'Write a Dockerfile',
      'Build an image',
      'Run a container',
      'Manage and scale the application',
    ],
  },
  {
    id: 'basic-workflow',
    title: 'Basic Workflow',
    paragraphs: [
      'The command cycle below appears repeatedly in real projects and is worth memorizing early.',
    ],
    commands: [
      {
        title: 'core workflow',
        command: 'docker build -t my-app .\ndocker run my-app\ndocker ps\ndocker stop <container_id>',
      },
    ],
  },
  {
    id: 'images',
    title: 'Docker Images',
    paragraphs: ['Images are the foundation of containerized systems.'],
    commands: [
      { title: 'list images', command: 'docker images' },
      { title: 'delete image', command: 'docker rmi <image_name>' },
      { title: 'prune unused images', command: 'docker image prune' },
      { title: 'build image', command: 'docker build -t <image_name>:<version> .' },
      { title: 'build without cache', command: 'docker build -t <image_name>:<version> . --no-cache' },
    ],
  },
  {
    id: 'containers',
    title: 'Docker Containers',
    paragraphs: [
      'Containers are running applications. If an image is missing locally, Docker pulls it automatically from a registry.',
    ],
    commands: [
      { title: 'list all containers', command: 'docker ps -a' },
      { title: 'list running containers', command: 'docker ps' },
      { title: 'run container', command: 'docker run <image_name>' },
      { title: 'run in detached mode', command: 'docker run -d <image_name>' },
      { title: 'run with custom name', command: 'docker run --name <container_name> <image_name>' },
    ],
  },
  {
    id: 'ports-env',
    title: 'Ports and Environment Variables',
    commands: [
      { title: 'bind ports', command: 'docker run -p <host_port>:<container_port> <image_name>' },
      { title: 'port mapping example', command: 'docker run -p 8080:80 nginx' },
      { title: 'set environment variable', command: 'docker run -e <var_name>=<var_value> <container_name>' },
    ],
  },
  {
    id: 'management',
    title: 'Container Management',
    commands: [
      { title: 'start container', command: 'docker start <container_name>' },
      { title: 'stop container', command: 'docker stop <container_name>' },
      { title: 'inspect container', command: 'docker inspect <container_name>' },
      { title: 'remove container', command: 'docker rm <container_name>' },
    ],
  },
  {
    id: 'debugging',
    title: 'Debugging and Troubleshooting',
    paragraphs: ['These commands are essential for runtime visibility and container-level debugging.'],
    commands: [
      { title: 'view logs', command: 'docker logs <container_name>' },
      { title: 'enter shell (bash)', command: 'docker exec -it <container_name> /bin/bash' },
      { title: 'enter shell (sh)', command: 'docker exec -it <container_name> sh' },
    ],
  },
  {
    id: 'docker-hub',
    title: 'Docker Hub Registry',
    paragraphs: ['Docker Hub is the default public registry for pulling and sharing images.'],
    commands: [
      { title: 'pull image', command: 'docker pull <image_name>' },
      { title: 'push image', command: 'docker push <username>/<image_name>' },
      { title: 'login', command: 'docker login' },
      { title: 'logout', command: 'docker logout' },
      { title: 'search images', command: 'docker search <image_name>' },
    ],
  },
  {
    id: 'volumes',
    title: 'Volumes and Persistent Storage',
    paragraphs: ['Containers are ephemeral by default. Volumes keep stateful data durable.'],
    commands: [
      { title: 'list volumes', command: 'docker volume ls' },
      { title: 'create volume', command: 'docker volume create <volume_name>' },
      { title: 'remove volume', command: 'docker volume rm <volume_name>' },
      { title: 'mount named volume', command: 'docker run -v <volume_name>:<mount_path>' },
      {
        title: 'mount using --mount',
        command: 'docker run --mount type=volume,src=<volume_name>,dest=<mount_path>',
      },
      { title: 'anonymous volume', command: 'docker run -v <mount_path>' },
      { title: 'bind mount', command: 'docker run -v <host_path>:<container_path>' },
      { title: 'prune unused volumes', command: 'docker volume prune' },
    ],
  },
  {
    id: 'networking',
    title: 'Docker Networks',
    paragraphs: ['Networking controls how multiple containers communicate in a system.'],
    commands: [
      { title: 'list networks', command: 'docker network ls' },
      { title: 'create network', command: 'docker network create <network_name>' },
      { title: 'remove network', command: 'docker network rm <network_name>' },
      { title: 'prune unused networks', command: 'docker network prune' },
    ],
  },
  {
    id: 'compose',
    title: 'Docker Compose Essentials',
    paragraphs: ['Compose is the fastest way to run multi-service stacks during development and testing.'],
    commands: [
      { title: 'start services in background', command: 'docker compose up -d' },
      { title: 'stop and remove stack', command: 'docker compose down' },
      { title: 'stream compose logs', command: 'docker compose logs -f' },
      { title: 'list compose services', command: 'docker compose ps' },
    ],
  },
  {
    id: 'cleanup-disk',
    title: 'Cleanup and Disk Usage',
    commands: [
      { title: 'check docker disk usage', command: 'docker system df' },
      { title: 'aggressive cleanup', command: 'docker system prune -a --volumes' },
    ],
  },
  {
    id: 'inspect-debug',
    title: 'Inspection and Runtime Debugging',
    commands: [
      { title: 'readable container table', command: 'docker ps --format "table {{.Names}}\\t{{.Status}}\\t{{.Ports}}"' },
      { title: 'inspect published ports', command: "docker inspect --format '{{json .NetworkSettings.Ports}}' <container_name>" },
      { title: 'live CPU and memory metrics', command: 'docker stats' },
      { title: 'follow logs with tail', command: 'docker logs -f --tail 100 <container_name>' },
      { title: 'watch docker events', command: 'docker events' },
    ],
  },
  {
    id: 'file-transfer-backup',
    title: 'Files, Backup, and Portability',
    commands: [
      { title: 'copy file from container', command: 'docker cp <container_name>:/path/in/container ./local-path' },
      { title: 'copy file to container', command: 'docker cp ./local-file <container_name>:/path/in/container' },
      { title: 'save image as tar', command: 'docker save -o image.tar <image_name>' },
      { title: 'load image from tar', command: 'docker load -i image.tar' },
      { title: 'export container filesystem', command: 'docker export <container_name> -o container.tar' },
    ],
  },
  {
    id: 'image-details',
    title: 'Image Metadata and Tagging',
    commands: [
      { title: 'show image history', command: 'docker history <image_name>' },
      { title: 'show image digests', command: 'docker image ls --digests' },
      { title: 'create additional tag', command: 'docker tag <image_name> <repo_name>:<tag>' },
    ],
  },
  {
    id: 'real-world',
    title: 'Real-World Understanding',
    paragraphs: ['Docker becomes most valuable when used in end-to-end application workflows.'],
    bullets: [
      'Run backend services with Django, Spring Boot, or Node.js',
      'Connect application containers to PostgreSQL or MySQL',
      'Use Docker Compose for multi-service local development',
      'Ship consistent deployments to cloud servers',
      'Create reproducible onboarding environments for teams',
    ],
  },
  {
    id: 'final-thoughts',
    title: 'Final Thoughts',
    paragraphs: [
      'Docker may feel unfamiliar at first, but it quickly becomes one of the most practical tools in modern engineering workflows.',
      'Start with one service, then add storage, networking, and eventually multi-container setups. That progression builds real confidence.',
      'If this guide helped, keep it bookmarked as your daily command reference.',
    ],
  },
];

function DockerGuidePage() {
  const [isDark, setIsDark] = useState(getInitialBlogTheme);
  const [shareCopyState, setShareCopyState] = useState('idle');
  const exploreBlogs = getExploreBlogs('docker-guide');
  const readTime = estimateReadTimeFromSections(articleSections, [
    'A complete beginner-to-practical walkthrough of Docker, followed by a structured command reference you can use daily.',
  ]);

  const shareUrl = 'https://supanroy.com/blogs/docker-guide';
  const shareText = 'Docker: From Fundamentals to Practical Usage (Complete Guide) by Supan Roy';

  useEffect(() => {
    document.title = 'Supan Roy | Blogs';
  }, []);

  useEffect(() => {
    window.localStorage.setItem(BLOG_THEME_STORAGE_KEY, isDark ? 'dark' : 'light');
  }, [isDark]);

  const copyTextWithFallback = async (text) => {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const copied = document.execCommand('copy');
    document.body.removeChild(textArea);
    return copied;
  };

  const handleCopyShareLink = async () => {
    try {
      const copied = await copyTextWithFallback(shareUrl);
      setShareCopyState(copied ? 'success' : 'error');
      setTimeout(() => setShareCopyState('idle'), 1800);
    } catch (error) {
      setShareCopyState('error');
      setTimeout(() => setShareCopyState('idle'), 1800);
    }
  };

  return (
    <div
      className={`min-h-screen font-sans transition-colors duration-500 ${
        isDark
          ? 'dark bg-gradient-to-b from-[#0a0f1c] via-[#0f172a] to-[#151f33]'
          : 'bg-gradient-to-b from-slate-50 via-white to-slate-100'
      }`}
    >
      <header className="sticky top-0 z-20 border-b border-slate-300/80 bg-slate-100/95 backdrop-blur dark:border-white/10 dark:bg-[#0a1220]/90 dark:supports-[backdrop-filter]:bg-[#0a1220]/75">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" className="transition hover:opacity-90" aria-label="Go to home page">
            <img src="/favicon.svg" alt="SR Logo" className="h-8 w-8 rounded-md" />
          </Link>

          <div className="flex items-center gap-3">
            <Link
              to="/blogs"
              className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-slate-700 transition hover:border-slate-400 hover:bg-slate-100 dark:border-white/20 dark:text-slate-100 dark:hover:border-white/40 dark:hover:bg-white/10"
            >
              Blogs
            </Link>
            <button
              type="button"
              onClick={() => setIsDark((prev) => !prev)}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              className={`rounded-full border p-2.5 transition ${
                isDark
                  ? 'border-amber-300/70 bg-amber-100 text-amber-700 hover:bg-amber-200'
                  : 'border-slate-800/70 bg-slate-900 text-white hover:bg-slate-800'
              }`}
            >
              {isDark ? <FiSun size={16} /> : <FiMoon size={16} />}
            </button>
          </div>
        </div>
      </header>

      <BlogArticleShell sections={articleSections} relatedBlogs={exploreBlogs}>
        <article className="animate-fade-up">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-sky-700 dark:text-sky-300/90">Technical Blog</p>
          <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-slate-900 dark:text-white sm:text-5xl">
            <img
              src="https://cdn.simpleicons.org/docker/2496ED"
              alt="Docker logo"
              className="mr-2 inline-block h-10 w-10 align-middle"
            />
            <span className="align-middle">Docker: From Fundamentals to Practical Usage (Complete Guide)</span>
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-600 dark:text-slate-300">
            <span className="font-semibold text-slate-900 dark:text-slate-100">Supan Roy</span>
            <span>30 Mar, 2026</span>
            <span>{readTime} min read</span>
            <span>23:11</span>
          </div>

          <p className="mt-8 text-lg leading-relaxed text-slate-700 dark:text-slate-200">
            A complete beginner-to-practical walkthrough of Docker, followed by a structured command reference you can use daily.
          </p>

          {articleSections.map((section, sectionIndex) => (
            <section
              key={section.id}
              id={section.id}
              className="mt-12 scroll-mt-28 animate-fade-up"
              style={{ animationDelay: `${Math.min(sectionIndex * 70, 560)}ms` }}
            >
              <h2 className="mb-4 text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl">{section.title}</h2>

              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph} className="mt-4 text-base leading-relaxed text-slate-700 dark:text-slate-200 sm:text-lg">
                  {paragraph}
                </p>
              ))}

              {section.bullets?.length ? (
                <ul className="mt-5 list-disc space-y-3 pl-6 text-base leading-relaxed text-slate-700 dark:text-slate-200 sm:text-lg">
                  {section.bullets.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}

              {section.ordered?.length ? (
                <ol className="mt-5 list-decimal space-y-3 pl-6 text-base leading-relaxed text-slate-700 dark:text-slate-200 sm:text-lg">
                  {section.ordered.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ol>
              ) : null}

              {section.subSections?.map((subSection) => (
                <div key={subSection.title} className="mt-7 rounded-xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/[0.03]">
                  <h3 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">{subSection.title}</h3>
                  {subSection.paragraphs?.map((paragraph) => (
                    <p key={paragraph} className="mt-3 text-base leading-relaxed text-slate-700 dark:text-slate-200 sm:text-lg">
                      {paragraph}
                    </p>
                  ))}
                </div>
              ))}

              {section.commands?.map((codeBlock) => (
                <TerminalBlock key={`${section.id}-${codeBlock.title}`} title={codeBlock.title} command={codeBlock.command} />
              ))}
            </section>
          ))}

          <section className="mt-14 scroll-mt-28">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Share this blog</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
              If this guide helped, share it with your network.
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-2 sm:gap-3">
              <a
                href={`https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 no-underline transition hover:border-slate-400 hover:bg-slate-200 hover:no-underline dark:border-white/20 dark:bg-white/[0.04] dark:text-slate-200 dark:hover:border-white/40 dark:hover:bg-white/[0.1] dark:hover:text-white"
              >
                <FaXTwitter size={15} />
                <span>Share on X</span>
              </a>

              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 no-underline transition hover:border-slate-400 hover:bg-slate-200 hover:no-underline dark:border-white/20 dark:bg-white/[0.04] dark:text-slate-200 dark:hover:border-white/40 dark:hover:bg-white/[0.1] dark:hover:text-white"
              >
                <FaLinkedin size={15} />
                <span>Share on LinkedIn</span>
              </a>

              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 no-underline transition hover:border-slate-400 hover:bg-slate-200 hover:no-underline dark:border-white/20 dark:bg-white/[0.04] dark:text-slate-200 dark:hover:border-white/40 dark:hover:bg-white/[0.1] dark:hover:text-white"
              >
                <FaFacebook size={15} />
                <span>Share on Facebook</span>
              </a>

              <button
                type="button"
                onClick={handleCopyShareLink}
                className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-900 dark:border-slate-300 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
              >
                <FiLink size={15} />
                <span>
                  {shareCopyState === 'success' ? 'Copied!' : shareCopyState === 'error' ? 'Copy failed' : 'Copy link'}
                </span>
              </button>
            </div>
          </section>

          <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-[#0f172a]/80">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Connect with me</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
              Follow for more technical updates and projects.
            </p>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <a
                href="https://x.com/supanroy0"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 no-underline transition hover:border-slate-400 hover:bg-slate-100 hover:no-underline dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-white/30 dark:hover:bg-white/[0.06]"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-black text-white">
                  <FaXTwitter size={16} />
                </span>
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">X</span>
              </a>

              <a
                href="https://linkedin.com/in/supanroy"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 no-underline transition hover:border-slate-400 hover:bg-slate-100 hover:no-underline dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-white/30 dark:hover:bg-white/[0.06]"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#0A66C2] text-white">
                  <FaLinkedin size={16} />
                </span>
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">LinkedIn</span>
              </a>

              <a
                href="https://facebook.com/supan.being.roy"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 no-underline transition hover:border-slate-400 hover:bg-slate-100 hover:no-underline dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-white/30 dark:hover:bg-white/[0.06]"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#1877F2] text-white">
                  <FaFacebook size={16} />
                </span>
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">Facebook</span>
              </a>

              <a
                href="https://github.com/Supan-Roy"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 no-underline transition hover:border-slate-400 hover:bg-slate-100 hover:no-underline dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-white/30 dark:hover:bg-white/[0.06]"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#111827] text-white">
                  <FaGithub size={16} />
                </span>
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">GitHub</span>
              </a>
            </div>
          </section>
        </article>
      </BlogArticleShell>
    </div>
  );
}

export default DockerGuidePage;
